import {Injectable} from '@angular/core';
import cytoscape, {EdgeSingular, NodeSingular} from 'cytoscape';
import {buildLinkShapeSvg, buildNodeShapeSrcForContextMenu, svgToDataUri} from '../shared/helpers/legend-shape.helper';
import {ToursService} from '../api/services/tour.service';
import {MapStateService} from '../api/services/map-state.service';
import {ViewService} from '../api/services/view.service';
import {PackageService} from '../api/services/package.service';
import {CytoscapeService} from '../services/cy-services/cytoscape.service';
import {ButtonPressService} from '../services/button-press.service';
import {GraphMap, GraphNode, GraphNodeLink} from '../api/interfaces/graph-node.interface';
import {DefaultValues, ModelElementType, PACKAGE_NAMES} from '@maporium-workspace/shared';
import {concatMap, delay, firstValueFrom, from, mergeMap, of, switchMap, take, timer,} from 'rxjs';
import {MatDialog} from '@angular/material/dialog';
import {TranslateService} from '@ngx-translate/core';
import {ConfirmationDialogComponent} from '../dialogs/confirmation-dialog.component';
import {get, reverse} from 'lodash';
import {updateInProgress} from '../shared/helpers/signal.helpers';
import {getTopLevelParents} from '../services/undo-redo.service';
import {
  CommandFactoryService,
  CompositeCommand,
  CreateLinkCommand,
  GraphLinkSnapshot,
  ReverseLinkCommand,
  ReverseLinkSnapshot,
  UndoRedoManagerService,
  undoRedoStateChanged$
} from '../services/undo-redo';
import {MapRoleService} from '../acl/map-role.service';
import {isInIframe} from '../shared/helpers/maporium.validators';
import {
  centeringInProgress,
  deletePanZoomState,
  ExpandCollapseInProgress,
  invalidateGeneralPanZoom,
  invalidatePanZoomForCollapsedNode,
  restorePanZoomForCollapsedNode,
  restoreSavedPanZoomForCollapsedNode,
  setPanZoomForCollapsedNode
} from '../../cytoscape-extensions/pan-zoom-storage';
import {MapService} from '../api/services/map.service';
import {UserService} from '../api/services/user.service';
import {SettingsService} from '../api/services/setting.service';
import {DatatifyDirective} from '../shared/directives/datatify.directive';
import {LinkTarget} from '../shared/pipes/link-target/link-target.enum';
import {SidebarService} from '../sidebar-form/sidebar.service';
import {OpenaiService} from '../api/services/openai.service';
import {TTSService} from '../services/tts.service';
import {NotificationService} from '../services/notification.service';
import {doubleTapExtension, getDoubleTapSetting} from '../../cytoscape-extensions/double-tap.extension';
import {DashboardComponent} from './dashboard.component';
import {JobService} from '../api/services/job.service';
import {NodeService} from '../api/services/node.service';
import {ModelService} from '../api/services/model.service';
import {DefaultStyleService} from '../api/services/default-style.service';
import {
  callAddAnchor,
  clearEdgeEditingData,
  getPointDataFromAnchorsArray,
} from '../shared/helpers/edge-editing.helper';

@Injectable({
  providedIn: 'root'
})
export class DashboardService {

  constructor(
    private toursService: ToursService,
    private mapStateService: MapStateService,
    private viewService: ViewService,
    private packageService: PackageService,
    private mapRoleService: MapRoleService,
    private cyService: CytoscapeService,
    private buttonPressService: ButtonPressService,
    private jobService: JobService,
    private mapService: MapService,
    private nodeService: NodeService,
    private userService: UserService,
    private settingsService: SettingsService,
    private sidebarService: SidebarService,
    private openaiService: OpenaiService,
    private ttsService: TTSService,
    private modelService: ModelService,
    private defaultStyleService: DefaultStyleService,
    private undoRedoManager: UndoRedoManagerService,
    private commandFactory: CommandFactoryService,
    private matDialog: MatDialog,
    private notificationService: NotificationService,
    private translate: TranslateService
  ) {
  }

  /**
   * Build the confirmation message for deletion
   */
  private buildDeletionMessage(nodeCount: number, linkCount: number, smapName: string): string {
    const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
    const undoKeystroke = isMac
      ? this.translate.instant('DASHBOARD_MSG.CONFIRM.UNDO_KEYSTROKE_MAC')
      : this.translate.instant('DASHBOARD_MSG.CONFIRM.UNDO_KEYSTROKE_OTHER');

    let elementsPart = '';
    if (nodeCount > 0 && linkCount > 0) {
      const nodeText = nodeCount === 1
        ? this.translate.instant('DASHBOARD_MSG.CONFIRM.NODE_SINGULAR')
        : this.translate.instant('DASHBOARD_MSG.CONFIRM.NODE_PLURAL', { count: nodeCount });
      const linkText = linkCount === 1
        ? this.translate.instant('DASHBOARD_MSG.CONFIRM.LINK_SINGULAR')
        : this.translate.instant('DASHBOARD_MSG.CONFIRM.LINK_PLURAL', { count: linkCount });
      elementsPart = this.translate.instant('DASHBOARD_MSG.CONFIRM.ELEMENTS_COMBINED', { nodeText, linkText });
    } else if (nodeCount > 0) {
      elementsPart = nodeCount === 1
        ? this.translate.instant('DASHBOARD_MSG.CONFIRM.NODE_SINGULAR')
        : this.translate.instant('DASHBOARD_MSG.CONFIRM.NODE_PLURAL', { count: nodeCount });
    } else if (linkCount > 0) {
      elementsPart = linkCount === 1
        ? this.translate.instant('DASHBOARD_MSG.CONFIRM.LINK_SINGULAR')
        : this.translate.instant('DASHBOARD_MSG.CONFIRM.LINK_PLURAL', { count: linkCount });
    }

    return this.translate.instant('DASHBOARD_MSG.CONFIRM.DELETE_MESSAGE', { elements: elementsPart, smapName, undoKeystroke });
  }

  /**
   * Show deletion confirmation dialog
   * Returns Promise that resolves to true if confirmed, false if cancelled
   */
  private showDeletionConfirmation(nodeCount: number, linkCount: number, smapName: string): Promise<boolean> {
    return new Promise((resolve) => {
      const message = this.buildDeletionMessage(nodeCount, linkCount, smapName);
      const dialogRef = this.matDialog.open(ConfirmationDialogComponent, {
        width: '540px',
        maxWidth: '90vw',
        data: {
          title: this.translate.instant('DASHBOARD_MSG.CONFIRM.DELETE_TITLE'),
          icon: 'delete',
          message: message,
          confirmText: this.translate.instant('DASHBOARD_MSG.CONFIRM.DELETE_CONFIRM'),
          cancelText: this.translate.instant('DASHBOARD_MSG.CONFIRM.CANCEL')
        }
      });

      dialogRef.afterClosed().pipe(take(1)).subscribe((result) => {
        resolve(result?.confirmed === true);
      });
    });
  }

  private canManageModels(): boolean {
    // Check if user has required service plan
    const hasRequiredPackage = this.packageService.hasPackage(PACKAGE_NAMES.creator) ||
                               this.packageService.hasPackage(PACKAGE_NAMES.publisher) ||
                               this.packageService.hasPackage(PACKAGE_NAMES.analyst) ||
                               this.packageService.hasPackage(PACKAGE_NAMES.developer);

    // Check if user has required map role (owner, manager, or editor)
    const hasRequiredRole = this.mapRoleService.hasCurrentUserPermission('edit') ||
                            this.mapRoleService.hasCurrentUserPermission('manage');

    return hasRequiredPackage && hasRequiredRole;
  }

  // Store pending link model for application during edge creation
  private pendingLinkModel: any = null
  private sourceNodeForModel: string | null = null

  async initCtxMenu2(
    cy: cytoscape.Core,
    edgeHandles: any,
    contextParams: {
      hasNoMap: boolean;
      currentMap: GraphMap | undefined;
      hasMapPermission: (permission: any) => boolean;
      selectedNodes: any[];
      selectedLinks: any[];
      // Component methods that need to be called
      centerCanvas: () => void;
      centerOnTarget: (cy: cytoscape.Core, target: any) => void;
      updateNode: (res: any, updateMap: boolean) => any;
      resetAllSelected: (cy: cytoscape.Core) => void;
      addNodeToParent: (element: any, position: any) => void;
      moveNodeOneLevelUp: (node: any) => any;
      removeNodeFromParent: (node: any) => any;
      addCyLink: (cy: cytoscape.Core, res: any) => any;
      initAnchors: (links: any[], type: string) => void;
      // Component properties
      createFromMenu: boolean;
      cy: cytoscape.Core;
      isUnlinking: boolean;
      isChangingLinkSource: boolean;
      hasNodesNoNodes: boolean;
      contextMenuInstance: any;
      menuSubscription: any;
      undoRedoSubscription: any;
      edgeEditingInstance: any;
      undoRedoManager: UndoRedoManagerService;
      settingsService: any;
      appWideStateService: any;
      // Static references
      DashboardComponent: any;
      componentInstance: any;
    }
  ) {
    if (contextParams.hasNoMap) return;

    // Load models asynchronously to prevent race condition (but not in iframe mode)
    let currentModels: any = { nodeModels: [], linkModels: [] };
    if (contextParams.currentMap?.id && !isInIframe()) {
      try {
        currentModels = await this.modelService.getAll(contextParams.currentMap.id).toPromise();
      } catch (error) {
        console.warn('Error loading models for context menu:', error);
      }
    }

    let toursMenu: any = await this.toursService.getTourCtxMenuItems();
    // Determine if user can access base state (is owner or has edit permission)
    const currentUser = this.userService.getCurrentUserFromStorage();
    const isOwner = contextParams.currentMap?.owner?.id === currentUser?.id;
    const canAccessBaseState = isOwner || contextParams.hasMapPermission('edit');
    let stateMenu: any = await this.mapStateService.getCtxMenuItems(canAccessBaseState);
    let viewMenu: any = await this.viewService.getCtxMenuItems();

    const options = () => {
      const menu = {
        evtType: 'cxttap',
        submenuIndicator: { src: 'assets/mat-symbols/outlined/chevron_right.svg', width: 12, height: 12, x: 5, y: 6 },
        menuItems: (this.packageService.isDefaulter() || ((contextParams.currentMap?.isReadonly || isInIframe()) && !contextParams.hasMapPermission('edit'))) ?
          // READ ONLY MENU
          [
            {
              id: 'center_canvas',
              content: this.translate.instant('DASHBOARD_MSG.MENU.CENTER'),
              image: { src: 'assets/mat-symbols/outlined/center_focus_weak.svg', width: 12, height: 12, x: 5, y: 6 },
              coreAsWell: true,
              onClickFunction: () => {
                contextParams.centerCanvas();
              },
              hasTrailingDivider: true
            },
            {
              id: 'Collapse',
              content: this.translate.instant('DASHBOARD_MSG.MENU.COLLAPSE'),
              selector: 'node:parent',
              image: { src: 'assets/mat-symbols/outlined/unfold_less.svg', width: 12, height: 12, x: 5, y: 6 },
              onClickFunction: (e: any) => {
                const targetNode = e.target;
                const selectedCyNodes = cy.$('node:selected');
                const nodesToCollapse = selectedCyNodes.length > 0 && selectedCyNodes.contains(targetNode)
                  ? selectedCyNodes
                  : cy.collection([targetNode]);

                this.cyService.collapseMultipleNodes(nodesToCollapse.toArray() as NodeSingular[], false, false)
                  .pipe(
                    switchMap((res) => {
                      if (res && !isInIframe()) {
                        return contextParams.updateNode(res, true);
                      } else {
                        return of(null);
                      }
                    })
                  )
                  .subscribe(() => {
                    // Redraw labels after async operations complete
                    if (this.cyService.linkLabelRenderer) {
                      this.cyService.linkLabelRenderer.redraw();
                    }
                  });
              }
            },
            {
              id: 'Expand',
              content: this.translate.instant('DASHBOARD_MSG.MENU.EXPAND'),
              selector: 'node.collapsed',
              image: { src: 'assets/mat-symbols/outlined/unfold_more.svg', width: 12, height: 12, x: 5, y: 6 },
              onClickFunction: (e: any) => {
                const targetNode = e.target;
                const selectedNodes = cy.$('node:selected');
                const nodesToExpand = selectedNodes.length > 0 && selectedNodes.contains(targetNode)
                  ? selectedNodes
                  : cy.collection([targetNode]);

                this.cyService.expandMultipleNodes(nodesToExpand.toArray() as NodeSingular[], false, false)
                  .subscribe((res: any) => {
                    if (res && !isInIframe()) {
                      contextParams.updateNode(res, true).subscribe(() => {
                        // Redraw labels after async operations complete
                        if (this.cyService.linkLabelRenderer) {
                          this.cyService.linkLabelRenderer.redraw();
                        }
                      });
                    } else {
                      // Redraw labels even if no update needed
                      if (this.cyService.linkLabelRenderer) {
                        this.cyService.linkLabelRenderer.redraw();
                      }
                    }
                  });
              },
              hasTrailingDivider: true
            },
            {
              id: 'Center',
              content: this.translate.instant('DASHBOARD_MSG.MENU.CENTER'),
              selector: 'node',
              image: { src: 'assets/mat-symbols/outlined/center_focus_weak.svg', width: 12, height: 12, x: 5, y: 6 },
              hasTrailingDivider: true,
              onClickFunction: (event: any) => {
                const selectedElements = cy.$(':selected');
                invalidateGeneralPanZoom();
                invalidatePanZoomForCollapsedNode();
                if (selectedElements.length > 0) {
                  this.cyService.centerOnElements(cy, selectedElements);
                } else {
                  contextParams.centerOnTarget(cy, event.target);
                }
              }
            },
            {
              id: 'center_on_source',
              content: this.translate.instant('DASHBOARD_MSG.MENU.CENTER_SOURCE'),
              image: { src: 'assets/mat-symbols/outlined/line_start.svg', width: 12, height: 12, x: 5, y: 6 },
              selector: 'edge',
              onClickFunction: (event: any) => contextParams.centerOnTarget(cy, event.target.source())
            },
            {
              id: 'center_on_target',
              content: this.translate.instant('DASHBOARD_MSG.MENU.CENTER_TARGET'),
              selector: 'edge',
              image: { src: 'assets/mat-symbols/outlined/line_end.svg', width: 12, height: 12, x: 5, y: 6 },
              onClickFunction: (event: any) => contextParams.centerOnTarget(cy, event.target.target())
            },
            {
              id: 'center_canvas_2',
              content: this.translate.instant('DASHBOARD_MSG.MENU.CENTER'),
              image: { src: 'assets/mat-symbols/outlined/center_focus_weak.svg', width: 12, height: 12, x: 5, y: 6 },
              selector: 'edge',
              hasTrailingDivider: true,
              onClickFunction: (event: any) => {
                contextParams.centerOnTarget(cy, event.target);
              }
            }
          ].filter(Boolean)
          :
          // FULL EDIT MENU - This would be the complete menu implementation
          this.buildFullEditMenu(cy, edgeHandles, contextParams, currentModels)
      };

      if (stateMenu !== null) {
        menu.menuItems.push(stateMenu);
      }

      if (viewMenu !== null) {
        menu.menuItems.push(viewMenu);
      }

      if (toursMenu !== null) {
        menu.menuItems.push(toursMenu);
      }

      return menu;
    };

    const originalContextMenus = cy.contextMenus;
    cy.contextMenus = function(action) {
      const originalResult = originalContextMenus.call(this, action);
      if (action === 'get') {
        //@ts-ignore
        originalResult.getOptions = function() {
          return options();
        };
      }
      return originalResult;
    };

    // IMPORTANT: contextParams is a plain object built by the caller — mutating
    // contextParams.menuSubscription/contextMenuInstance does NOT propagate back
    // to DashboardComponent. That leak is what made every map switch stack a new
    // permanent subscription on CytoscapeService.menuChangedSubject, fanning each
    // emit into N×(views+tours+state+models) HTTP calls and eventually exhausting
    // Chrome's socket pool (ERR_INSUFFICIENT_RESOURCES). Always read/write via
    // componentInstance so the lifecycle survives re-inits.
    const host = contextParams.componentInstance;

    if (host?.contextMenuInstance) {
      try {
        host.contextMenuInstance.destroy();
      } catch {
        // already destroyed
      }
      host.contextMenuInstance = null;
    }

    //@ts-ignore
    contextParams.contextMenuInstance = cy.contextMenus(options());
    if (host) host.contextMenuInstance = contextParams.contextMenuInstance;

    if (host?.menuSubscription) {
      host.menuSubscription.unsubscribe();
      host.menuSubscription = undefined;
    }

    const menuSub = CytoscapeService.menuChangedSubject.subscribe(async () => {
      if (host?.contextMenuInstance) {
        try {
          host.contextMenuInstance.destroy();
        } catch {
          // Ignore destroy errors - menu may already be destroyed
        }
        host.contextMenuInstance = null;
      }
      viewMenu = await this.viewService.getCtxMenuItems();
      toursMenu = await this.toursService.getTourCtxMenuItems();
      // Re-compute canAccessBaseState for menu update
      const currentUserForMenu = this.userService.getCurrentUserFromStorage();
      const isOwnerForMenu = contextParams.currentMap?.owner?.id === currentUserForMenu?.id;
      const canAccessBaseStateForMenu = isOwnerForMenu || contextParams.hasMapPermission('edit');
      stateMenu = contextParams.appWideStateService.readMode() ? null : await this.mapStateService.getCtxMenuItems(canAccessBaseStateForMenu);

      // Reload models for menu update (but not in iframe mode)
      if (contextParams.currentMap?.id && !isInIframe()) {
        try {
          currentModels = await this.modelService.getAll(contextParams.currentMap.id).toPromise();
        } catch (error) {
          console.warn('Error reloading models for context menu:', error);
        }
      }

      //@ts-ignore
      const rebuilt = cy.contextMenus(options());
      contextParams.contextMenuInstance = rebuilt;
      if (host) host.contextMenuInstance = rebuilt;
    });
    contextParams.menuSubscription = menuSub;
    if (host) host.menuSubscription = menuSub;

    // Also rebuild the context menu whenever the models cache updates — e.g. on
    // smap switches (legend-panel + managers already react to this, but the
    // context menu was stuck on the previous map's models) or when the
    // assistant adds missing legends via the models API. Signature-compared to
    // avoid rebuilding on identical emissions.
    // MAP-1569: include updatedAt so an in-place model edit (same id set, e.g.
    // renamed or restyled via the model dialog's "update based on selection
    // focus" toggle) is detected too, not just additions/removals.
    const signatureOf = (m: { nodeModels: any[]; linkModels: any[] }): string =>
      m.nodeModels.map((x: any) => `${x.id}:${x.updatedAt}`).sort().join(',') + '|' +
      m.linkModels.map((x: any) => `${x.id}:${x.updatedAt}`).sort().join(',');
    let lastModelsSig = signatureOf(currentModels);
    menuSub.add(
      this.modelService.currentModelsSubject.subscribe((models) => {
        const sig = signatureOf(models);
        if (sig === lastModelsSig) return;
        lastModelsSig = sig;
        CytoscapeService.menuChangedSubject.next();
      })
    );

    // Lightweight subscription for undo/redo state changes only (no API calls, no menu rebuild)
    if (host?.undoRedoSubscription) {
      host.undoRedoSubscription.unsubscribe();
      host.undoRedoSubscription = undefined;
    }
    const undoRedoSub = undoRedoStateChanged$.subscribe(() => {
      this.updateUndoRedoMenuItems(host?.contextMenuInstance ?? contextParams.contextMenuInstance);
    });
    contextParams.undoRedoSubscription = undoRedoSub;
    if (host) host.undoRedoSubscription = undoRedoSub;

    // Handle URL metadata context menu
    this.setupMetadataContextMenu(cy, contextParams);
  }

  private buildCreateSubmenu(cy: cytoscape.Core, edgeHandles: any, contextParams: any, menuContext: 'canvas' | 'node' | 'link', currentModels: any): any[] {
    const submenuItems: any[] = [];

    // Determine which models to show based on context
    let showNodeModels = false;
    let showLinkModels = false;

    switch (menuContext) {
      case 'canvas':
        // Canvas shows only node models (links need a source node)
        showNodeModels = currentModels.nodeModels.length > 0;
        showLinkModels = false;
        break;
      case 'node':
        // Node context shows all models (user can create nodes and links from a node)
        showNodeModels = currentModels.nodeModels.length > 0;
        showLinkModels = currentModels.linkModels.length > 0;
        break;
      case 'link':
        // Link context shows only node models (can create nodes from links)
        showNodeModels = currentModels.nodeModels.length > 0;
        showLinkModels = false;
        break;
    }

    // Add node models
    if (showNodeModels) {
      currentModels.nodeModels.forEach((model: any) => {
        submenuItems.push({
          id: `create_from_node_model_${model.id}`,
          content: model.name,
          tooltipText: model.description || undefined,
          image: {
            src: buildNodeShapeSrcForContextMenu(model.elementDefinition?.originalElement, {
              size: 12,
              strokeWidth: 1.5
            }), width: 12, height: 12, x: 5, y: 6
          },
          onClickFunction: (event: any) => {
            const position = event.position;
            contextParams.createFromMenu = true;
            contextParams.resetAllSelected(cy);

            if (menuContext === 'node') {
              // Node context: add new node as child of the selected node
              const parentNode = event.target;
              this.cyService.createNodeFromModel(model, position, parentNode.id());
            } else {
              // Canvas/Link context: create node without parent
              this.cyService.createNodeFromModel(model, position);
            }
          }
        });
      });
    }

    // Add separator if both exist
    if (showNodeModels && showLinkModels && submenuItems.length > 0) {
      submenuItems[submenuItems.length - 1].hasTrailingDivider = true;
    }

    // Add link models
    if (showLinkModels) {
      currentModels.linkModels.forEach((model: any) => {
        submenuItems.push({
          id: `create_from_link_model_${model.id}`,
          content: model.name,
          tooltipText: model.description || undefined,
          image: {
            src: svgToDataUri(buildLinkShapeSvg(model.elementDefinition?.originalElement, {
              size: 14,
              strokeWidth: 1.5
            })), width: 14, height: 10, x: 4, y: 7
          },
          onClickFunction: (event: any) => {
            contextParams.createFromMenu = true;
            contextParams.resetAllSelected(cy);

            // Node context: use selected node as source (only context where link models appear)
            const sourceNode = event.target;
            this.createLinkFromModel(cy, edgeHandles, sourceNode, contextParams, model);
          }
        });
      });
    }

    return submenuItems;
  }

  private createLinkFromModel(cy: cytoscape.Core, edgeHandles: any, sourceNode: any, contextParams: any, linkModel: any) {
    // Start edge creation from the selected node (where context menu was opened)
    edgeHandles.start(sourceNode);

    // Store the link model to apply when edge is completed
    this.pendingLinkModel = linkModel;
    this.sourceNodeForModel = sourceNode.id();
  }

  private buildSelectAllSubmenu(idSuffix: string): any[] {
    return [
      {
        id: `select_all_elements_${idSuffix}`,
        content: this.translate.instant('DASHBOARD_MSG.MENU.SELECT_ALL_ELEMENTS'),
        hasTrailingDivider: true,
        onClickFunction: () => this.cyService.selectAllElements()
      },
      {
        id: `select_all_nodes_${idSuffix}`,
        content: this.translate.instant('DASHBOARD_MSG.MENU.SELECT_ALL_NODES'),
        onClickFunction: () => this.cyService.selectAllNodes()
      },
      {
        id: `select_all_links_${idSuffix}`,
        content: this.translate.instant('DASHBOARD_MSG.MENU.SELECT_ALL_LINKS'),
        onClickFunction: () => this.cyService.selectAllLinks()
      }
    ];
  }

  private buildFullEditMenu(cy: cytoscape.Core, edgeHandles: any, contextParams: any, currentModels: any): any[] {
    const menuItems = [];

    // Add Create... submenu for canvas context (not in iframe mode)
    const createSubmenuItems = this.buildCreateSubmenu(cy, edgeHandles, contextParams, 'canvas', currentModels);
    if (createSubmenuItems.length > 0 && this.canManageModels() && !isInIframe()) {
      menuItems.push({
        id: 'create_from_model',
        content: this.translate.instant('DASHBOARD_MSG.MENU.CREATE'),
        image: { src: 'assets/mat-symbols/outlined/add.svg', width: 12, height: 12, x: 5, y: 6 },
        coreAsWell: true,
        submenu: createSubmenuItems,
        hasTrailingDivider: false,
        onClickFunction: () => {} // Required for plugin
      });
    }

    return [
      ...menuItems,
      {
        id: 'create_node',
        content: this.translate.instant('DASHBOARD_MSG.MENU.CREATE_NODE'),
        image: { src: 'assets/mat-symbols/outlined/adjust.svg', width: 12, height: 12, x: 5, y: 6 },
        coreAsWell: true,
        hasTrailingDivider: !localStorage.getItem('ENABLE_RECTANGLE_FEATURE'),
        onClickFunction: (event: any) => {
          const position = event.position;
          contextParams.createFromMenu = true;
          contextParams.resetAllSelected(cy);
          this.cyService.createDefaultNode({ x: position.x, y: position.y });
        }
      },
      ...(localStorage.getItem('ENABLE_RECTANGLE_FEATURE') ? [{
        id: 'create_rectangle',
        content: this.translate.instant('DASHBOARD_MSG.MENU.ADD_RECTANGLE'),
        image: {src: 'assets/mat-symbols/outlined/rectangle.svg', width: 12, height: 12, x: 5, y: 6},
        coreAsWell: true,
        hasTrailingDivider: true,
        onClickFunction: (event: any) => {
          const position = event.position;
          if (contextParams.componentInstance?.createOverlayElement) {
            contextParams.componentInstance.createOverlayElement(position.x, position.y);
          }
        }
      }] : []),
      {
        id: 'undo',
        content: this.undoRedoManager.canUndo() ? this.translate.instant('DASHBOARD_MSG.MENU.UNDO_LABELED', { description: this.undoRedoManager.undoDescription() }) : this.translate.instant('TOOLBAR.UNDO'),
        image: { src: 'assets/mat-symbols/outlined/undo.svg', width: 12, height: 12, x: 5, y: 6 },
        coreAsWell: true,
        hasLeadingDivider: true,
        disabled: !this.undoRedoManager.canUndo(),
        onClickFunction: async () => {
          await this.undoRedoManager.undo();
        }
      },
      {
        id: 'redo',
        content: this.undoRedoManager.canRedo() ? this.translate.instant('DASHBOARD_MSG.MENU.REDO_LABELED', { description: this.undoRedoManager.redoDescription() }) : this.translate.instant('TOOLBAR.REDO'),
        image: {src: 'assets/mat-symbols/outlined/redo.svg', width: 12, height: 12, x: 5, y: 6},
        coreAsWell: true,
        hasTrailingDivider: true,
        disabled: !this.undoRedoManager.canRedo(),
        onClickFunction: async () => {
          await this.undoRedoManager.redo();
        }
      },
      {
        id: 'paste',
        content: this.translate.instant('DASHBOARD_MSG.MENU.PASTE'),
        image: { src: 'assets/mat-symbols/outlined/content_paste.svg', width: 12, height: 12, x: 5, y: 6 },
        coreAsWell: true,
        onClickFunction: (event: any) => {
          const hasNoId = event.target.data()?.id === undefined;
          if (hasNoId) {
            DashboardComponent.tappedNode = null;
            DashboardComponent.tappedLink = null;
          }
          this.buttonPressService.copyFromClipboard(
            event.target.data()?.id ? event.target.data() : undefined,
            this.buttonPressService.getContextPressPosition() ?? event.position
          );
        }
      },
      {
        id: 'select_all',
        content: this.translate.instant('DASHBOARD_MSG.MENU.SELECT_ALL'),
        image: { src: 'assets/mat-symbols/outlined/select_all.svg', width: 12, height: 12, x: 5, y: 6 },
        coreAsWell: true,
        hasTrailingDivider: true,
        submenu: this.buildSelectAllSubmenu('canvas'),
        onClickFunction: () => {} // Required for plugin
      },
      // {
      //   id: 'explain_map',
      //   content: 'Explain',
      //   image: { src: 'assets/mat-symbols/outlined/psychology.svg', width: 12, height: 12, x: 5, y: 6 },
      //   coreAsWell: true,
      //   hasLeadingDivider: true,
      //   hasTrailingDivider: true,
      //   disabled: !this.packageService.hasPackage(PACKAGE_NAMES.developer),
      //   onClickFunction: (event: any) => {
      //     this.handleExplainAction('map', null, event.position, contextParams);
      //   }
      // },
      {
        id: 'center_canvas',
        content: this.translate.instant('DASHBOARD_MSG.MENU.CENTER'),
        image: { src: 'assets/mat-symbols/outlined/center_focus_weak.svg', width: 12, height: 12, x: 5, y: 6 },
        coreAsWell: true,
        hasTrailingDivider: true,
        onClickFunction: () => {
          invalidateGeneralPanZoom();
          invalidatePanZoomForCollapsedNode();
          contextParams.centerCanvas();
        }
      },
      // NODE MENU
      // Add Create... submenu for node context (not in iframe mode)
      ...(() => {
        const nodeCreateSubmenuItems = this.buildCreateSubmenu(cy, edgeHandles, contextParams, 'node', currentModels);
        if (nodeCreateSubmenuItems.length > 0 && this.canManageModels() && !isInIframe()) {
          return [{
            id: 'create_from_model_node',
            content: this.translate.instant('DASHBOARD_MSG.MENU.CREATE'),
            image: { src: 'assets/mat-symbols/outlined/add.svg', width: 12, height: 12, x: 5, y: 6 },
            selector: 'node',
            submenu: nodeCreateSubmenuItems,
            hasTrailingDivider: false,
            onClickFunction: () => {} // Required for plugin
          }];
        }
        return [];
      })(),
      {
        id: 'add-node',
        content: this.translate.instant('DASHBOARD_MSG.MENU.CREATE_NODE'),
        image: { src: 'assets/mat-symbols/outlined/adjust.svg', width: 12, height: 12, x: 5, y: 6 },
        selector: 'node',
        onClickFunction: (event: any) => {
          const ele = event.target;
          if (ele.group() === 'nodes') {
            contextParams.addNodeToParent(ele, event.position);
          }
        }
      },
      {
        id: 'Link',
        content: this.translate.instant('DASHBOARD_MSG.MENU.CREATE_LINK'),
        selector: 'node',
        image: { src: 'assets/mat-symbols/outlined/linear_scale.svg', width: 12, height: 12, x: 5, y: 6 },
        hasTrailingDivider: true,
        onClickFunction: function(event: any) {
          edgeHandles.start(event.target);
        }
      },
      {
        id: 'undoNode',
        content: this.undoRedoManager.canUndo() ? this.translate.instant('DASHBOARD_MSG.MENU.UNDO_LABELED', { description: this.undoRedoManager.undoDescription() }) : this.translate.instant('TOOLBAR.UNDO'),
        selector: 'node',
        image: { src: 'assets/mat-symbols/outlined/undo.svg', width: 12, height: 12, x: 5, y: 6 },
        hasLeadingDivider: true,
        disabled: !this.undoRedoManager.canUndo(),
        onClickFunction: async () => {
          await this.undoRedoManager.undo();
        }
      },
      {
        id: 'redoNode',
        content: this.undoRedoManager.canRedo() ? this.translate.instant('DASHBOARD_MSG.MENU.REDO_LABELED', { description: this.undoRedoManager.redoDescription() }) : this.translate.instant('TOOLBAR.REDO'),
        selector: 'node',
        image: {src: 'assets/mat-symbols/outlined/redo.svg', width: 12, height: 12, x: 5, y: 6},
        hasTrailingDivider: true,
        disabled: !this.undoRedoManager.canRedo(),
        onClickFunction: async () => {
          await this.undoRedoManager.redo();
        }
      },
      {
        id: 'cut',
        content: this.translate.instant('DASHBOARD_MSG.MENU.CUT'),
        image: { src: 'assets/mat-symbols/outlined/content_cut.svg', width: 12, height: 12, x: 5, y: 6 },
        selector: 'node',
        onClickFunction: (event: any) => {
          event.target.select();
          this.buttonPressService.cutSelected(cy);
        }
      },
      {
        id: 'copy',
        content: this.translate.instant('DASHBOARD_MSG.MENU.COPY'),
        image: { src: 'assets/mat-symbols/outlined/content_copy.svg', width: 12, height: 12, x: 5, y: 6 },
        selector: 'node',
        onClickFunction: (event: any) => {
          event.target.select();
          this.buttonPressService.copySelected(cy);
        }
      },
      {
        id: 'paste_node',
        content: this.translate.instant('DASHBOARD_MSG.MENU.PASTE'),
        image: { src: 'assets/mat-symbols/outlined/content_paste.svg', width: 12, height: 12, x: 5, y: 6 },
        selector: 'node',
        onClickFunction: (event: any) => {
          this.buttonPressService.copyFromClipboard(
            event.target.data()?.id ? event.target.data() : undefined,
            this.buttonPressService.getContextPressPosition() ?? event.position
          );
        }
      },
      {
        id: 'Remove_node',
        content: this.translate.instant('DASHBOARD_MSG.MENU.DELETE'),
        selector: 'node',
        image: { src: 'assets/mat-symbols/outlined/delete.svg', width: 12, height: 12, x: 5, y: 6 },
        onClickFunction: async (event: any) => {
          const ele = event.target;
          const allSelected = cy.$(':selected');
          const nodes = cy.$('node:selected');
          const edges = cy.$('edge:selected');
          const topLvlNodes = getTopLevelParents(nodes as any);
          const topLvlNodesSet = new Set(topLvlNodes);
          const allSelectedNodes = allSelected
            .filter((n: any) => n.isNode())
            .filter((n: any) => topLvlNodesSet.has(n.id()));
          const allSelectedEdges = allSelected.filter((n: any) => n.isEdge());
          const allSelectedCombinedAfterFiltering = [...allSelectedNodes, ...allSelectedEdges];

          // Determine what we're actually deleting (must match deletion logic below)
          const deletingSelection = allSelectedCombinedAfterFiltering.length > 1;

          // Calculate total affected elements (including descendants and connected edges)
          let nodeCount = 0;
          const nodeIds = new Set<string>();
          const nodesToCount = deletingSelection
              ? allSelectedCombinedAfterFiltering.filter((e: any) => e.isNode())
              : [ele];
          for (const node of nodesToCount) {
            if (!nodeIds.has(node.id())) {
              nodeIds.add(node.id());
              nodeCount++;
            }
            const descendants = node.descendants();
            descendants.forEach((desc: any) => {
              if (!nodeIds.has(desc.id())) {
                nodeIds.add(desc.id());
                nodeCount++;
              }
            });
          }

          const linkIds = new Set<string>();
          // Only count selected edges if we're deleting the selection
          if (deletingSelection) {
            for (const edge of allSelectedEdges) {
              linkIds.add(edge.id());
            }
          }
          for (const node of nodesToCount) {
            const allNodes = node.union(node.descendants());
            const connectedEdges = allNodes.connectedEdges();
            connectedEdges.forEach((edge: any) => {
              linkIds.add(edge.id());
            });
          }
          const linkCount = linkIds.size;

          // Show confirmation if at least 2 elements are affected
          const totalAffected = nodeCount + linkCount;
          if (totalAffected >= 2) {
            const currentMap = this.mapService.getCurrentSelectedMapFromStore();
            const smapName = currentMap?.name || this.translate.instant('DASHBOARD_MSG.CONFIRM.DEFAULT_SMAP_NAME');
            const shouldProceed = await this.showDeletionConfirmation(nodeCount, linkCount, smapName);
            if (!shouldProceed) {
              return;
            }
          }

          // Collect all IDs including descendants and connected edges that will be removed
          const allIdsToTrack = new Set<string>();
          const elementsToDelete = allSelectedCombinedAfterFiltering.length > 1
            ? allSelectedCombinedAfterFiltering
            : [ele];

          for (const element of elementsToDelete) {
            allIdsToTrack.add(element.data('id'));
            // If it's a node, also track its descendants and connected edges
            if (element.isNode()) {
              const descendants = element.descendants();
              descendants.forEach((desc: any) => {
                allIdsToTrack.add(desc.data('id'));
              });
              const allNodes = element.union(descendants);
              const connectedEdges = allNodes.connectedEdges();
              connectedEdges.forEach((edge: any) => {
                allIdsToTrack.add(edge.data('id'));
              });
            }
          }

          // Capture snapshot before deletion for undo
          const snapshot = this.commandFactory.captureElementSnapshot(Array.from(allIdsToTrack));

          // Create and execute delete command
          const deleteCommand = this.commandFactory.createDeleteCommand(snapshot);
          await this.undoRedoManager.executeCommand(deleteCommand);

          const hasNodes = cy.nodes().length > 0;
          if (!hasNodes) {
            contextParams.hasNodesNoNodes = true;
          }
          contextParams.selectedNodes = [];
          DashboardComponent.tappedNode = null;
          DashboardComponent.tappedLink = null;

          nodes.data('selected', false);
          edges.data('selected', false);
          nodes.removeClass('highlighted');
          edges.removeClass('highlighted');

          allSelected.unselect();
        }
      },
      {
        id: 'select_all_node',
        content: this.translate.instant('DASHBOARD_MSG.MENU.SELECT_ALL'),
        selector: 'node',
        image: { src: 'assets/mat-symbols/outlined/select_all.svg', width: 12, height: 12, x: 5, y: 6 },
        hasTrailingDivider: true,
        submenu: this.buildSelectAllSubmenu('node'),
        onClickFunction: () => {} // Required for plugin
      },
      // Explain is not fully implemented yet — only expose it (and its separator) on test plans.
      // The preceding Select all item already renders a trailing divider, so no leading divider here
      // (avoids a doubled separator that shifts the menu).
      ...(this.packageService.isOnTestPlan() ? [{
        id: 'explain_node',
        content: this.translate.instant('DASHBOARD_MSG.MENU.EXPLAIN'),
        selector: 'node',
        image: { src: 'assets/mat-symbols/outlined/psychology.svg', width: 12, height: 12, x: 5, y: 6 },
        hasTrailingDivider: true,
        onClickFunction: (event: any) => {
          this.explainNode(event.target.data());
        }
      }] : []),
      {
        id: 'Expand',
        content: this.translate.instant('DASHBOARD_MSG.MENU.EXPAND'),
        selector: 'node.collapsed',
        image: { src: 'assets/mat-symbols/outlined/unfold_more.svg', width: 12, height: 12, x: 5, y: 6 },
        onClickFunction: (event: any) => {
          const targetNode = event.target;
          const selectedNodes = cy.$('node:selected');
          const nodesToExpand = selectedNodes.length > 0 && selectedNodes.contains(targetNode)
            ? selectedNodes
            : cy.collection([targetNode]);

          this.cyService.expandMultipleNodes(nodesToExpand.toArray() as NodeSingular[], true, false)
            .subscribe((res: any) => {
              if (res && !isInIframe()) {
                contextParams.updateNode(res, true).subscribe(() => {
                  // Redraw labels after async operations complete
                  if (this.cyService.linkLabelRenderer) {
                    this.cyService.linkLabelRenderer.redraw();
                  }
                });
              } else {
                // Redraw labels even if no update needed
                if (this.cyService.linkLabelRenderer) {
                  this.cyService.linkLabelRenderer.redraw();
                }
              }
            });
        }
      },
      {
        id: 'Collapse',
        content: this.translate.instant('DASHBOARD_MSG.MENU.COLLAPSE'),
        selector: 'node:parent',
        image: { src: 'assets/mat-symbols/outlined/unfold_less.svg', width: 12, height: 12, x: 5, y: 6 },
        onClickFunction: (event: any) => {
          const targetNode = event.target;
          const selectedNodes = cy.$('node:selected');
          const nodesToCollapse = selectedNodes.length > 0 && selectedNodes.contains(targetNode)
            ? selectedNodes
            : cy.collection([targetNode]);

          this.cyService.collapseMultipleNodes(nodesToCollapse.toArray() as NodeSingular[], true, false)
            .pipe(
              switchMap((res) => {
                if (res && !isInIframe()) {
                  return contextParams.updateNode(res, true);
                } else {
                  return of(null);
                }
              })
            )
            .subscribe(() => {
              // Redraw labels after async operations complete
              if (this.cyService.linkLabelRenderer) {
                this.cyService.linkLabelRenderer.redraw();
              }
            });
        }
      },
      {
        id: 'Center',
        content: this.translate.instant('DASHBOARD_MSG.MENU.CENTER'),
        selector: 'node',
        image: { src: 'assets/mat-symbols/outlined/center_focus_weak.svg', width: 12, height: 12, x: 5, y: 6 },
        hasTrailingDivider: true,
        onClickFunction: (event: any) => {
          invalidatePanZoomForCollapsedNode();
          invalidateGeneralPanZoom();
          const selectedElements = cy.$(':selected');
          if (selectedElements.length > 0) {
            this.cyService.centerOnElements(cy, selectedElements);

          } else {
            contextParams.centerOnTarget(cy, event.target);
          }
        }
      },
      {
        id: 'Move_Up',
        content: this.translate.instant('DASHBOARD_MSG.MENU.EXTRACT_FROM_CONTAINER'),
        selector: 'node',
        disabled: this.mapStateService.getCurrentLocalState()?.id !== undefined,
        image: { src: 'assets/mat-symbols/outlined/step_out.svg', width: 12, height: 12, x: 5, y: 6 },
        onClickFunction: (ele: any) => {
          if (ele.target.group() === 'nodes') {
            const selectedElements = cy.$('node:selected');
            if (selectedElements.length > 0) {
              const nodesToMove = selectedElements.filter((node: NodeSingular) => {
                const parent = node.parent();
                return !selectedElements.contains(parent);
              });
              from(nodesToMove)
                .pipe(
                  switchMap((node: NodeSingular) => {
                    return contextParams.moveNodeOneLevelUp(node);
                  })
                )
                .subscribe();
            } else {
              contextParams.moveNodeOneLevelUp(ele.target).subscribe();
            }
          }
        }
      },
      {
        id: 'Move_Top',
        content: this.translate.instant('DASHBOARD_MSG.MENU.EXTRACT_TO_CANVAS'),
        selector: 'node',
        image: { src: 'assets/mat-symbols/outlined/vertical_align_top.svg', width: 12, height: 12, x: 5, y: 6 },
        hasTrailingDivider: true,
        disabled: this.mapStateService.getCurrentLocalState()?.id !== undefined,
        onClickFunction: (ele: any) => {
          if (ele.target.group() === 'nodes') {
            const selectedElements = cy.$('node:selected');
            if (selectedElements.length > 0) {
              const nodesToMove = selectedElements.filter((node: NodeSingular) => {
                const parent = node.parent();
                return !selectedElements.contains(parent);
              });
              from(nodesToMove)
                .pipe(
                  switchMap((node: NodeSingular) => {
                    return contextParams.removeNodeFromParent(node);
                  })
                ).subscribe();
            } else {
              contextParams.removeNodeFromParent(ele.target).subscribe();
            }
          }
        }
      },
      // EDGE MENU
      // Add Create... submenu for edge context (not in iframe mode)
      ...(() => {
        const edgeCreateSubmenuItems = this.buildCreateSubmenu(cy, edgeHandles, contextParams, 'link', currentModels);
        if (edgeCreateSubmenuItems.length > 0 && this.canManageModels() && !isInIframe()) {
          return [{
            id: 'create_from_model_edge',
            content: this.translate.instant('DASHBOARD_MSG.MENU.CREATE'),
            image: { src: 'assets/mat-symbols/outlined/add.svg', width: 12, height: 12, x: 5, y: 6 },
            selector: 'edge',
            submenu: edgeCreateSubmenuItems,
            hasTrailingDivider: false,
            onClickFunction: () => {} // Required for plugin
          }];
        }
        return [];
      })(),
      {
        id: 'create_node_edge',
        content: this.translate.instant('DASHBOARD_MSG.MENU.CREATE_NODE'),
        image: { src: 'assets/mat-symbols/outlined/adjust.svg', width: 12, height: 12, x: 5, y: 6 },
        selector: 'edge',
        hasTrailingDivider: true,
        onClickFunction: (event: any) => {
          const position = event.position;
          contextParams.createFromMenu = true;
          contextParams.resetAllSelected(cy);
          this.cyService.createDefaultNode({ x: position.x, y: position.y });
        }
      },
      {
        id: 'undoEdge',
        content: this.undoRedoManager.canUndo() ? this.translate.instant('DASHBOARD_MSG.MENU.UNDO_LABELED', { description: this.undoRedoManager.undoDescription() }) : this.translate.instant('TOOLBAR.UNDO'),
        selector: 'edge',
        image: {src: 'assets/mat-symbols/outlined/undo.svg', width: 12, height: 12, x: 5, y: 6},
        hasLeadingDivider: true,
        disabled: !this.undoRedoManager.canUndo(),
        onClickFunction: async () => {
          await this.undoRedoManager.undo();
        }
      },
      {
        id: 'redoEdge',
        content: this.undoRedoManager.canRedo() ? this.translate.instant('DASHBOARD_MSG.MENU.REDO_LABELED', { description: this.undoRedoManager.redoDescription() }) : this.translate.instant('TOOLBAR.REDO'),
        selector: 'edge',
        image: {src: 'assets/mat-symbols/outlined/redo.svg', width: 12, height: 12, x: 5, y: 6},
        hasTrailingDivider: true,
        disabled: !this.undoRedoManager.canRedo(),
        onClickFunction: async () => {
          await this.undoRedoManager.redo();
        }
      },
      {
        id: 'Remove',
        content: this.translate.instant('DASHBOARD_MSG.MENU.DELETE'),
        selector: 'edge',
        image: { src: 'assets/mat-symbols/outlined/delete.svg', width: 12, height: 12, x: 5, y: 6 },
        onClickFunction: async (event: any) => {
          const selected = cy.$(':selected');
          const selectedEdges = selected.filter((e: any) => e.isEdge());
          const edgesToDelete = selectedEdges.length > 0 ? selectedEdges : cy.collection().add(event.target);
          const linkCount = edgesToDelete.length;

          // Show confirmation if at least 2 links are affected
          if (linkCount >= 2) {
            const currentMap = this.mapService.getCurrentSelectedMapFromStore();
            const smapName = currentMap?.name || this.translate.instant('DASHBOARD_MSG.CONFIRM.DEFAULT_SMAP_NAME');
            const shouldProceed = await this.showDeletionConfirmation(0, linkCount, smapName);
            if (!shouldProceed) {
              return;
            }
          }

          // Collect all link IDs
          const linkIds: string[] = [];
          edgesToDelete.forEach((edge: any) => {
            linkIds.push(edge.id());
          });

          // Capture snapshot before deletion
          const snapshot = this.commandFactory.captureElementSnapshot(linkIds);

          // Create and execute delete command
          updateInProgress.set(true);
          const deleteCommand = this.commandFactory.createDeleteCommand(snapshot);
          await this.undoRedoManager.executeCommand(deleteCommand);
        }
      },
      {
        id: 'select_all_edge',
        content: this.translate.instant('DASHBOARD_MSG.MENU.SELECT_ALL'),
        selector: 'edge',
        image: { src: 'assets/mat-symbols/outlined/select_all.svg', width: 12, height: 12, x: 5, y: 6 },
        hasTrailingDivider: true,
        submenu: this.buildSelectAllSubmenu('edge'),
        onClickFunction: () => {} // Required for plugin
      },
      // TODO: Explain Link
      // {
      //   id: 'explain_link',
      //   content: 'Explain',
      //   selector: 'edge',
      //   image: { src: 'assets/mat-symbols/outlined/psychology.svg', width: 12, height: 12, x: 5, y: 6 },
      //   hasLeadingDivider: true,
      //   hasTrailingDivider: true,
      //   disabled: !this.packageService.hasPackage(PACKAGE_NAMES.developer),
      //   onClickFunction: (event: any) => {
      //    this.explainLink(event.target.data());
      //   }
      // },
      {
        id: 'center_on_source',
        content: this.translate.instant('DASHBOARD_MSG.MENU.CENTER_SOURCE'),
        image: { src: 'assets/mat-symbols/outlined/line_start.svg', width: 12, height: 12, x: 5, y: 6 },
        selector: 'edge',
        onClickFunction: (event: any) => {
          const selectedEdges = cy.$('edge:selected');
          if (selectedEdges.length > 0) {
            const sources = selectedEdges.map((edge: EdgeSingular) => edge.source());
            const selector = sources.map((s: any) => `#${s.id()}`).join(', ');
            const elements = cy.$(selector);
            this.cyService.centerOnElements(cy, elements);
          } else {
            contextParams.centerOnTarget(cy, event.target.source());
          }
        }
      },
      {
        id: 'center_on_target',
        content: this.translate.instant('DASHBOARD_MSG.MENU.CENTER_TARGET'),
        selector: 'edge',
        image: { src: 'assets/mat-symbols/outlined/line_end.svg', width: 12, height: 12, x: 5, y: 6 },
        onClickFunction: (event: any) => {
          const selectedEdges = cy.$('edge:selected');
          if (selectedEdges.length > 0) {
            const targets = selectedEdges.map((edge: EdgeSingular) => edge.target());
            const selector = targets.map((s: any) => `#${s.id()}`).join(', ');
            const elements = cy.$(selector);
            this.cyService.centerOnElements(cy, elements);
          } else {
            contextParams.centerOnTarget(cy, event.target.target());
          }
        }
      },
      {
        id: 'center_canvas_2',
        content: this.translate.instant('DASHBOARD_MSG.MENU.CENTER'),
        image: { src: 'assets/mat-symbols/outlined/center_focus_weak.svg', width: 12, height: 12, x: 5, y: 6 },
        selector: 'edge',
        hasTrailingDivider: true,
        onClickFunction: (event: any) => {
          const selectedEdges = cy.$(':selected');
          if (selectedEdges.length > 0) {
            this.cyService.centerOnElements(cy, selectedEdges);
          } else {
            contextParams.centerOnTarget(cy, event.target);
          }
        }
      },
      {
        id: 'Change Link Source',
        content: this.translate.instant('DASHBOARD_MSG.MENU.CHANGE_SOURCE'),
        disabled: this.mapStateService.getCurrentLocalState()?.id !== undefined,
        selector: 'edge',
        image: { src: 'assets/mat-symbols/outlined/line_start_circle.svg', width: 12, height: 12, x: 5, y: 6 },
        onClickFunction: (event: any) => {
          const ele = event.target;
          const edge = ele as unknown as cytoscape.EdgeSingular;
          const originalSourceId = edge.source().id();
          const originalTargetId = edge.target().id();
          const source = edge.target() as cytoscape.NodeSingular;
          const edgeData = edge.data();
          const linkId = edgeData.id;
          const restoreLinkData = { ...edgeData, from: originalSourceId, to: originalTargetId };
          delete edgeData.source;

          edge.remove();
          // Set flags on component instance so handleNodeLinking can see them
          contextParams.componentInstance.isUnlinking = true;
          contextParams.componentInstance.isChangingLinkSource = true;
          contextParams.isUnlinking = true;
          contextParams.isChangingLinkSource = true;
          //@ts-ignore
          edgeHandles.start(source);
          let retargetCompleted = false;
          //@ts-ignore
          const cancelEvent = () => {
            if (retargetCompleted) return;
            const restored = contextParams.addCyLink(cy, restoreLinkData);
            if (restored) {
              contextParams.initAnchors([restored], restored.data('type'));
              restored.select();
              DashboardComponent.tappedLink = restored.data();
            }
            contextParams.componentInstance.isUnlinking = false;
            contextParams.componentInstance.isChangingLinkSource = false;
            contextParams.isUnlinking = false;
            contextParams.isChangingLinkSource = false;
            //@ts-ignore
            cy.off('ehcomplete', linkEvent);
            //@ts-ignore
            cy.off('ehcancel', cancelEvent);
          };
          //@ts-ignore
          const linkEvent = (event, sourceNode, targetNode, addedEdge) => {
            if (!contextParams.isUnlinking && !contextParams.isChangingLinkSource) return;
            retargetCompleted = true;
            //@ts-ignore
            cy.off('ehcancel', cancelEvent);

            let newEdgeData = addedEdge.data();
            newEdgeData = { ...newEdgeData, ...edgeData };
            addedEdge.data(newEdgeData);
            addedEdge.style('line-color', newEdgeData.color);
            addedEdge.style('target-arrow-color', newEdgeData.color);
            const payload: GraphNodeLink = {
              from: targetNode.data().id,
              to: sourceNode.data().id,
              type: newEdgeData.type,
              shape: newEdgeData.shape,
              id: newEdgeData.id,
              metadata: newEdgeData.metadata,
              textFontSize: newEdgeData.textFontSize,
              name: newEdgeData.name,
              direction: newEdgeData.direction || DefaultValues.link.direction,
              color: newEdgeData.color,
              weight: newEdgeData.weight,
              controlPointPositions: newEdgeData.controlPointPositions,
              bendPointPositions: newEdgeData.bendPointPositions
            };

            this.nodeService.updateLink(payload).subscribe((res: any) => {
              const newLink = contextParams.addCyLink(cy, res);
              if (!newLink) return;
              addedEdge.remove();
              const previousSelected = cy.$(':selected');

              // Clear selection and set new link as focused
              DashboardComponent.tappedLink = null;
              DashboardComponent.tappedNode = null;
              DashboardComponent.multiSelectedElements = [];
              contextParams.initAnchors([newLink], newLink.data('type'));
              newLink.select();
              DashboardComponent.tappedLink = newLink.data();

              // Create undo command for change source
              const newSourceId = targetNode.data().id;
              const changeSourceSnapshot: ReverseLinkSnapshot = {
                linkId,
                beforeSourceId: originalSourceId,
                beforeTargetId: originalTargetId,
                afterSourceId: newSourceId,
                afterTargetId: originalTargetId
              };
              const changeSourceCommand = new ReverseLinkCommand(
                this.cyService,
                this.nodeService,
                changeSourceSnapshot,
                'Source'
              );
              this.undoRedoManager.pushCommand(changeSourceCommand);

              setTimeout(() => {
                previousSelected.unselect();
              }, 100);

              // Reset flags with delay to prevent race conditions (matching Change Target behavior)
              setTimeout(() => {
                contextParams.componentInstance.isUnlinking = false;
                contextParams.componentInstance.isChangingLinkSource = false;
                contextParams.isUnlinking = false;
                contextParams.isChangingLinkSource = false;
              }, 1500);

              //@ts-ignore
              cy.off('ehcomplete', linkEvent);
            });
          };
          //@ts-ignore
          cy.on('ehcomplete', linkEvent);
          //@ts-ignore
          cy.on('ehcancel', cancelEvent);
        }
      },
      {
        selector: 'edge',
        id: 'Change_Target',
        content: this.translate.instant('DASHBOARD_MSG.MENU.CHANGE_TARGET'),
        disabled: this.mapStateService.getCurrentLocalState()?.id !== undefined,
        image: { src: 'assets/mat-symbols/outlined/line_end_circle.svg', width: 12, height: 12, x: 5, y: 6 },
        onClickFunction: (event: any) => {
          const ele = event.target;
          const edge = ele as unknown as cytoscape.EdgeSingular;
          const originalSourceId = edge.source().id();
          const originalTargetId = edge.target().id();
          const source = edge.source() as cytoscape.NodeSingular;
          const edgeData = edge.data();
          const linkId = edgeData.id;
          const restoreLinkData = { ...edgeData, from: originalSourceId, to: originalTargetId };
          delete edgeData.target;

          edge.remove();
          // Set flags on component instance so handleNodeLinking can see them
          contextParams.componentInstance.isUnlinking = true;
          contextParams.isUnlinking = true;
          //@ts-ignore
          edgeHandles.start(source);
          let retargetCompleted = false;
          //@ts-ignore
          const cancelEvent = () => {
            if (retargetCompleted) return;
            const restored = contextParams.addCyLink(cy, restoreLinkData);
            if (restored) {
              contextParams.initAnchors([restored], restored.data('type'));
              restored.select();
              DashboardComponent.tappedLink = restored.data();
            }
            contextParams.componentInstance.isUnlinking = false;
            contextParams.isUnlinking = false;
            //@ts-ignore
            cy.off('ehcomplete', linkEvent);
            //@ts-ignore
            cy.off('ehcancel', cancelEvent);
          };
          //@ts-ignore
          const linkEvent = (event, sourceNode, targetNode, addedEdge) => {
            if (!contextParams.isUnlinking) return;
            retargetCompleted = true;
            //@ts-ignore
            cy.off('ehcancel', cancelEvent);
            let newEdgeData = addedEdge.data();
            newEdgeData = { ...newEdgeData, ...edgeData };
            addedEdge.data(newEdgeData);
            addedEdge.style('line-color', newEdgeData.color);
            addedEdge.style('target-arrow-color', newEdgeData.color);
            const payload: GraphNodeLink = {
              from: sourceNode.data().id,
              to: targetNode.data().id,
              type: newEdgeData.type,
              shape: newEdgeData.shape,
              id: newEdgeData.id,
              textFontSize: newEdgeData.textFontSize,
              direction: newEdgeData.direction || DefaultValues.link.direction,
              metadata: newEdgeData.metadata,
              name: newEdgeData.name,
              weight: newEdgeData.weight,
              color: newEdgeData.color,
              controlPointPositions: newEdgeData.controlPointPositions,
              bendPointPositions: newEdgeData.bendPointPositions
            };
            this.nodeService.updateLink(payload).subscribe((res: any) => {
              const newLink = contextParams.addCyLink(cy, res);
              if (!newLink) return;
              addedEdge.remove();
              const previousSelected = cy.$(':selected');
              DashboardComponent.tappedLink = null;
              DashboardComponent.tappedNode = null;
              DashboardComponent.multiSelectedElements = [];
              contextParams.initAnchors([newLink], newLink.data('type'));
              newLink.select();
              DashboardComponent.tappedLink = newLink.data();

              // Create undo command for change target
              const newTargetId = targetNode.data().id;
              const changeTargetSnapshot: ReverseLinkSnapshot = {
                linkId,
                beforeSourceId: originalSourceId,
                beforeTargetId: originalTargetId,
                afterSourceId: originalSourceId,
                afterTargetId: newTargetId
              };
              const changeTargetCommand = new ReverseLinkCommand(
                this.cyService,
                this.nodeService,
                changeTargetSnapshot,
                'Target'
              );
              this.undoRedoManager.pushCommand(changeTargetCommand);

              setTimeout(() => {
                previousSelected.unselect();

              }, 100)
              setTimeout(() => {
                contextParams.isUnlinking = false;
                contextParams.componentInstance.isUnlinking = false;
              },1500)
              //@ts-ignore
              cy.off('ehcomplete', linkEvent);
            });
          };
          //@ts-ignore
          cy.on('ehcomplete', linkEvent);
          //@ts-ignore
          cy.on('ehcancel', cancelEvent);
        }
      },
      {
        id: 'Reverse Direction',
        content: this.translate.instant('DASHBOARD_MSG.MENU.REVERSE'),
        selector: 'edge',
        disabled: this.mapStateService.getCurrentLocalState()?.id !== undefined,
        image: { src: 'assets/mat-symbols/outlined/multiple_stop.svg', width: 12, height: 12, x: 5, y: 6 },
        onClickFunction: (event: any) => {
          const linksToUpdate: any[] = [];
          const isMetaEdge = (edge: any) =>
            edge.hasClass('cy-expand-collapse-meta-edge') || !!edge.data('originalEnds');
          // When an endpoint is hidden inside a collapsed container the visible edge is a
          // meta edge routed to the collapsed ancestor. Resolve the real (hidden) endpoints
          // from originalEnds so the underlying link is the one actually reversed.
          const resolveRealEnds = (edge: any): { sourceId: string; targetId: string } => {
            const originalEnds = edge.data('originalEnds');
            if (originalEnds?.source && originalEnds?.target) {
              return { sourceId: originalEnds.source.id(), targetId: originalEnds.target.id() };
            }
            return { sourceId: edge.data('source'), targetId: edge.data('target') };
          };
          // originalEnds/collapsedEdges hold live cytoscape Collections (cyclic + functions)
          // that structuredClone chokes on; strip them before cloning the payload.
          const cleanEdgeData = (data: any) => {
            const cleaned: any = {};
            for (const key in data) {
              if (key === 'originalEnds' || key === 'collapsedEdges' || key === 'collapsedChildren' || key.startsWith('_')) continue;
              const value = data[key];
              if (value === undefined || typeof value === 'function') continue;
              cleaned[key] = value;
            }
            return structuredClone(cleaned);
          };
          const flipMetaEdgeInPlace = (edge: any) => {
            const originalEnds = edge.data('originalEnds');
            if (originalEnds?.source && originalEnds?.target) {
              edge.data('originalEnds', { source: originalEnds.target, target: originalEnds.source });
            }
            const visibleSource = edge.data('source');
            const visibleTarget = edge.data('target');
            edge.move({ source: visibleTarget, target: visibleSource }).addClass('cy-expand-collapse-meta-edge');
          };
          // Reversing swaps the ends, so the anchor arrays have to be read back
          // to front. Doing it on the payload BEFORE the edge is drawn keeps
          // cytoscape from rendering one frame of the mirrored shape.
          const reverseAnchorsInPlace = (link: any) => {
            if (!link) {
              return link;
            }
            if (link.type === 'STRAIGHT' && Array.isArray(link.bendPointPositions)) {
              link.bendPointPositions = reverse([...link.bendPointPositions]);
            } else if (link.type === 'UNBUNDLED_BEZIER' && Array.isArray(link.controlPointPositions)) {
              link.controlPointPositions = reverse(structuredClone(link.controlPointPositions));
            }
            return link;
          };
          const anchorTypeOf = (link: any) =>
            link?.type === 'BEZIER' ? 'none' : link?.type === 'STRAIGHT' ? 'bend' : 'control';
          const reverseLink = (edge: EdgeSingular) => {
            const { sourceId, targetId } = resolveRealEnds(edge);
            const edgeData = cleanEdgeData(edge.data());
            edgeData.source = targetId;
            edgeData.target = sourceId;

            linksToUpdate.push({
              from: targetId,
              to: sourceId,
              ...edgeData
            });
          };

          const selectedEdges = cy.$('edge:selected');

          if (selectedEdges.length > 0) {
            // Capture original source/target for each edge before reversing
            const originalSnapshots: { linkId: string; sourceId: string; targetId: string }[] = [];
            const metaEdgesToFlip: any[] = [];
            selectedEdges.forEach((ele: any) => {
              const { sourceId, targetId } = resolveRealEnds(ele);
              originalSnapshots.push({
                linkId: ele.id(),
                sourceId,
                targetId
              });
              if (isMetaEdge(ele)) {
                metaEdgesToFlip.push(ele);
              }
              reverseLink(ele);
            });
            const newLinks: any[] = [];
            from(linksToUpdate).pipe(
              mergeMap((link) => this.nodeService.updateLink(link)),
              concatMap((res) => {
                newLinks.push(contextParams.addCyLink(cy, reverseAnchorsInPlace(res)));
                return timer(100);
              })
            ).subscribe(() => {
              // Collapsed meta edges have no addCyLink counterpart (endpoint hidden); flip in place.
              metaEdgesToFlip.forEach((ele: any) => flipMetaEdgeInPlace(ele));
              const drawnLinks = newLinks.filter(Boolean);
              drawnLinks.forEach((ele: any) => {
                if (contextParams.edgeEditingInstance) {
                  contextParams.edgeEditingInstance.initAnchorPoints(drawnLinks, anchorTypeOf(ele.data()));
                }
                this.nodeService.updateLink(ele.data()).subscribe();
                ele.select();
              });

              // Create undo commands for each reversed edge
              const reverseCommands = originalSnapshots.map(original => {
                const snapshot: ReverseLinkSnapshot = {
                  linkId: original.linkId,
                  beforeSourceId: original.sourceId,
                  beforeTargetId: original.targetId,
                  afterSourceId: original.targetId,
                  afterTargetId: original.sourceId
                };
                return new ReverseLinkCommand(this.cyService, this.nodeService, snapshot, 'Direction');
              });

              // Use CompositeCommand for multiple edges
              if (reverseCommands.length > 1) {
                const compositeCommand = new CompositeCommand(
                  'reverse-link',
                  `Reverse ${reverseCommands.length} links`,
                  reverseCommands
                );
                this.undoRedoManager.pushCommand(compositeCommand);
              } else if (reverseCommands.length === 1) {
                this.undoRedoManager.pushCommand(reverseCommands[0]);
              }

              // Labels read rscratch.allpts, refreshed only on the next render.
              this.cyService.redrawLinkLabelsAfterRender();

              // Ensure proper selection clearing with timeout to handle async events
              setTimeout(() => {
                DashboardComponent.tappedNode = null;
                DashboardComponent.multiSelectedElements = [];
              }, 50);
            });
          } else {
            const oldEdge = event.target;
            const { sourceId: originalSource, targetId: originalTarget } = resolveRealEnds(oldEdge);
            const oldEdgeIsMeta = isMetaEdge(oldEdge);

            // Unselect the old edge before reversal to prevent auto-selection issues
            oldEdge.unselect();

            reverseLink(oldEdge);
            this.nodeService.updateLink(linksToUpdate[0] as any)
              .subscribe((res: any) => {
                const newEdge = contextParams.addCyLink(cy, reverseAnchorsInPlace(res));
                contextParams.isUnlinking = false;

                // Collapsed meta edge: no visible re-add possible (endpoint hidden), flip in place.
                if (oldEdgeIsMeta) {
                  flipMetaEdgeInPlace(oldEdge);
                }

                // Handle control point reversal for the single edge case
                if (newEdge) {
                  if (contextParams.edgeEditingInstance) {
                    contextParams.edgeEditingInstance.initAnchorPoints([newEdge], anchorTypeOf(newEdge.data()));
                  }
                  this.nodeService.updateLink(newEdge.data()).subscribe();
                  newEdge.select();

                  // Create ReverseLinkCommand for undo/redo
                  const reverseLinkSnapshot: ReverseLinkSnapshot = {
                    linkId: res.id,
                    beforeSourceId: originalSource,
                    beforeTargetId: originalTarget,
                    afterSourceId: originalTarget,
                    afterTargetId: originalSource
                  };
                  const reverseLinkCommand = new ReverseLinkCommand(
                    this.cyService,
                    this.nodeService,
                    reverseLinkSnapshot,
                    'Direction'
                  );
                  this.undoRedoManager.pushCommand(reverseLinkCommand);

                  // Trigger label redraw to update HTML edge labels
                  if (this.cyService.linkLabelRenderer) {
                    this.cyService.linkLabelRenderer.redraw();
                  }
                }

                // Ensure proper selection clearing with timeout to handle async events
                setTimeout(() => {
                  DashboardComponent.tappedNode = null;
                  DashboardComponent.multiSelectedElements = [];
                }, 50);
              });
          }
          contextParams.isUnlinking = true;
        },
        hasTrailingDivider: true
      },
      // Set Default Style menu items at the bottom with separator
      // Canvas (SMAP) style
      {
        id: 'set_default_style_smap',
        content: this.translate.instant('DASHBOARD_MSG.MENU.SET_DEFAULT_SMAP_STYLE'),
        image: { src: 'assets/mat-symbols/outlined/tune.svg', width: 12, height: 12, x: 5, y: 6 },
        coreAsWell: true,
        hasLeadingDivider: true,
        disabled: !this.canManageModels(),
        onClickFunction: () => {
          // Set current map as default SMAP style
          this.defaultStyleService.setDefaultStyle(ModelElementType.SMAP, undefined, contextParams.currentMap?.id).subscribe({
            next: () => {
              this.notificationService.success(this.translate.instant('DASHBOARD_MSG.DEFAULT_STYLE.SMAP_SAVED'));
            },
            error: (error: any) => {
              this.notificationService.error(this.translate.instant('DASHBOARD_MSG.DEFAULT_STYLE.SAVE_FAILED'));
              console.error('Error setting default style:', error);
            }
          });
        }
      },
      // Node style
      {
        id: 'set_default_style_node',
        content: this.translate.instant('DASHBOARD_MSG.MENU.SET_DEFAULT_NODE_STYLE'),
        selector: 'node',
        image: { src: 'assets/mat-symbols/outlined/tune.svg', width: 12, height: 12, x: 5, y: 6 },
        hasLeadingDivider: true,
        disabled: !this.canManageModels(),
        onClickFunction: (event: any) => {
          const nodeId = event.target.id();
          // Set selected node as default NODE style
          this.defaultStyleService.setDefaultStyle(ModelElementType.NODE, nodeId, contextParams.currentMap?.id).subscribe({
            next: () => {
              this.notificationService.success(this.translate.instant('DASHBOARD_MSG.DEFAULT_STYLE.NODE_SAVED'));
            },
            error: (error: any) => {
              this.notificationService.error(this.translate.instant('DASHBOARD_MSG.DEFAULT_STYLE.SAVE_FAILED'));
              console.error('Error setting default style:', error);
            }
          });
        }
      },
      // Link style
      {
        id: 'set_default_style_link',
        content: this.translate.instant('DASHBOARD_MSG.MENU.SET_DEFAULT_LINK_STYLE'),
        selector: 'edge',
        image: { src: 'assets/mat-symbols/outlined/tune.svg', width: 12, height: 12, x: 5, y: 6 },
        hasLeadingDivider: true,
        disabled: !this.canManageModels(),
        onClickFunction: (event: any) => {
          const linkId = event.target.id();
          // Set selected link as default LINK style
          this.defaultStyleService.setDefaultStyle(ModelElementType.LINK, linkId, contextParams.currentMap?.id).subscribe({
            next: () => {
              this.notificationService.success(this.translate.instant('DASHBOARD_MSG.DEFAULT_STYLE.LINK_SAVED'));
            },
            error: (error: any) => {
              this.notificationService.error(this.translate.instant('DASHBOARD_MSG.DEFAULT_STYLE.SAVE_FAILED'));
              console.error('Error setting default style:', error);
            }
          });
        }
      }
    ];
  }


  private createNodeAndStartLinking(cy: cytoscape.Core, edgeHandles: any, position: any, contextParams: any) {
    // Create a default node at the click position to serve as link source
    this.cyService.createDefaultNode({ x: position.x, y: position.y });

    // Wait for the node to be created, then start linking
    setTimeout(() => {
      // Find the newly created node (it should be the last one added)
      const nodes = cy.nodes();
      const newNode = nodes[nodes.length - 1];

      if (newNode && newNode.length > 0) {
        // Start edge creation from the new node
        edgeHandles.start(newNode);
      }
    }, 200);
  }

  private openResource(url: string): void {
    const settings = this.settingsService.getLocalStorageSettings();
    const stored = Number(settings.linkTarget);
    const linkTarget = Number.isNaN(stored) ? LinkTarget.SingleWindow : stored;
    DatatifyDirective.onLinkClick(null, url, linkTarget);
  }

  private setupMetadataContextMenu(cy: cytoscape.Core, contextParams: any) {
    cy.on('cxttapstart', 'node, edge', (event: any) => {
      const target = event.target.data();
      if (!target?.metadata) return;

      const urls = target?.metadata.filter((m: any) => {
        try {
          new URL(m.value);
          return true;
        } catch {
          return false;
        }
      });

      const buildMenu = () => {
        return {
          id: 'Urls',
          content: this.translate.instant('DASHBOARD_MSG.MENU.OPEN_RESOURCE'),
          selector: 'edge,node',
          image: { src: 'assets/mat-symbols/outlined/language.svg', width: 12, height: 12, x: 5, y: 6 },
          submenu: urls.map((url: any) => {
            return {
              id: url.id,
              content: url.key,
              selector: 'edge,node',
              onClickFunction: () => this.openResource(url.value)
            };
          }),
          hasTrailingDivider: true
        };
      };

      const loadMenus = () => {
        try {
          contextParams.contextMenuInstance.removeMenuItem('Urls');
        } catch (e) {
          // ignore
        }
        try {
          //@ts-ignore
          contextParams.contextMenuInstance.appendMenuItem(buildMenu(), undefined);
        } catch (e) {
          // ignore
        }
      };

      if (urls.length > 0) {
        if (target?.direction !== undefined) {
          loadMenus();
        } else {
          loadMenus();
        }
      }
    });
  }

  async initializeMapAndUser(route: any): Promise<{hasNoMap: boolean, currentMap: GraphMap | undefined}> {
    // Note: refreshLocalUser() is called in dashboard.component ngOnInit (non-blocking)
    // to both refresh user data and check delinquent status after refresh completes.

    const resolvedMap = get(route.snapshot.data, 'currentMap');
    const storedMap = resolvedMap ?? this.mapService.getCurrentSelectedMapFromStore();
    let hasNoMap = this.mapService.hasNoStoredMap(storedMap?.id);

    let currentMap = hasNoMap ? undefined : storedMap as GraphMap;
    if (hasNoMap && !isInIframe()) {
      currentMap = this.mapService.getCurrentSelectedMapFromStore();
    } else if (isInIframe()) {
      currentMap = await firstValueFrom(this.mapService.findPublicMap(route.snapshot.queryParams['mapId']));
      hasNoMap = !currentMap;
    }

    return { hasNoMap, currentMap };
  }

  initializeCytoscapeExtensions(cy: cytoscape.Core): any {
    CytoscapeService.menuChangedSubject.next();
    doubleTapExtension(cy, {
      options: {
        doubleTapDelay: getDoubleTapSetting()
      }
    });

    // Hide built-in midpoint labels to avoid double text
    cy.style().selector('edge').style('label', '').update();

    return cy;
  }

  setupDoubleTapHandlers(cy: cytoscape.Core, context: {
    selectedNodes: any[];
    updateNode: (res: any, updateMap: boolean) => any;
    hasMapPermission: (permission: any) => boolean;
  }) {
    // MAP-1619: expanding or collapsing a container is a view action anyone may
    // perform, but PERSISTING the new collapsed state is a write. A commenter
    // double-tapping a container used to fire the node update anyway, and the
    // 403 sent them back to the previous smap with an access-denied alert. The
    // contextual menu never had the bug because it is permission-gated.
    const canPersistCollapse = () => !isInIframe() && context.hasMapPermission('edit');
    cy.on('doubleTap', 'node', (event: { target: any; }) => {
      const targetNode = event.target;
      const isCollapsed = targetNode.hasClass('collapsed');
      const selectedCyNodes = cy.nodes(':selected') as unknown as NodeSingular[];
      const storedNodePan = restorePanZoomForCollapsedNode(targetNode?.data('id'));

      if (isCollapsed) {
        // Save current pan/zoom state BEFORE expanding (pass true because node IS currently collapsed)
        setPanZoomForCollapsedNode(targetNode?.data('id'), true);

        // Start visual expand immediately without network calls (savePan: false to avoid overwriting saved state)
        this.cyService.expandMultipleNodes(selectedCyNodes, false, false);

        // Center on the expanded container
        this.cyService.centerOnElements(cy, targetNode, true, this.cyService.ZOOM_PERCENTAGE_CONTAINER);

        // Defer network updates to avoid blocking animation
        setTimeout(() => {
          this.cyService.expandMultipleNodes(selectedCyNodes, true, false)
            .pipe(
              switchMap((res) => {
                if (res && canPersistCollapse()) {
                  return context.updateNode(res, true);
                } else {
                  return of(null);
                }
              })
            )
            .subscribe();
        }, 50);
        // Exit early to prevent other centering logic
        return;

      } else if (targetNode.children().length > 0 && !storedNodePan) {
        // Check if animation already in progress to prevent overlapping animations
        if (ExpandCollapseInProgress() || centeringInProgress()) {
          return;
        }
        // Store current state (expanded container = collapsed: false)
        setPanZoomForCollapsedNode(targetNode?.data('id'), false);
        this.cyService.centerOnElements(cy, targetNode, true, this.cyService.ZOOM_PERCENTAGE_CONTAINER);

      } else {
        if (storedNodePan) {
          // Check if animation already in progress to prevent overlapping animations
          if (ExpandCollapseInProgress() || centeringInProgress()) {
            return;
          }
          if (storedNodePan.collapsed) {
            // Start visual collapse immediately, defer network update
            this.cyService.collapseNode(event, false);

            setTimeout(() => {
              this.cyService.collapseNode(event, true)
                .pipe(
                  switchMap((res) => {
                    if (res && canPersistCollapse()) {
                      return context.updateNode(res, true);
                    } else {
                      return of(null);
                    }
                  })
                )
                .subscribe();
            }, 50);
          }
          // Only restore pan/zoom if we're NOT expanding a collapsed container
          // For collapsed containers, we want to expand them, not restore old view
          if (!isCollapsed) {
            restoreSavedPanZoomForCollapsedNode(targetNode?.data('id'), cy, () => {
              invalidatePanZoomForCollapsedNode();
            });
          } else {
            // Just clear the stored state without animation for collapsed nodes
            invalidatePanZoomForCollapsedNode();
          }
        } else {
          setPanZoomForCollapsedNode(targetNode?.data('id'), isCollapsed);
          this.cyService.centerOnElements(cy, targetNode, true, this.cyService.ZOOM_PERCENTAGE_NODE);
        }
      }
    });

    cy.on('doubleTap', 'edge', (event: { target: any; }) => {
      const targetEdge = event.target;
      const edgeId = targetEdge?.data('id');
      if (ExpandCollapseInProgress() || centeringInProgress()) {
        return;
      }
      const storedEdgePan = restorePanZoomForCollapsedNode(edgeId);
      if (storedEdgePan) {
        restoreSavedPanZoomForCollapsedNode(edgeId, cy, () => {
          invalidatePanZoomForCollapsedNode();
        });
        return;
      }
      setPanZoomForCollapsedNode(edgeId, false);
      const framed = targetEdge.union(targetEdge.source()).union(targetEdge.target());
      this.cyService.centerOnElements(cy, framed, true, this.cyService.ZOOM_PERCENTAGE_LINK);
    });

    cy.on('doubleTap', (event: any) => {
      // Only handle background/canvas double-taps: nodes and edges have their own.
      // event.target is the element that emitted the doubleTap event (from the extension)
      const target = event.target;
      const isElement = typeof target.isNode === 'function' && (target.isNode() || target.isEdge());
      if (!isElement) {
        // Double-tap on canvas always goes to home, just like the home button
        // This clears all saved pan/zoom states and centers on all elements
        invalidateGeneralPanZoom();
        invalidatePanZoomForCollapsedNode();
        deletePanZoomState();
        this.cyService.centerCanvas(true, false, false, true, true);
      }
    });
  }

  setupEditModeConfiguration(currentMap: GraphMap | undefined) {
    const localSettings = this.settingsService.getLocalStorageSettings();
    const isDefaulter = this.packageService.isDefaulter();
    const editMode = (currentMap?.isReadonly || isDefaulter) ? false : Boolean(localSettings.isEditMode);
    editMode ? this.sidebarService.enableEditMode() : this.sidebarService.disableEditMode();
  }

  setupNodeLinking(cy: cytoscape.Core, context: {
    hasMapPermission: (permission: any) => boolean;
    componentInstance: any;
    nodeService: any;
    cyService: any;
    mapStateService: any;
    edgeEditingInstance:any;
    linkEndpointSignal: () => any;
    addCyLink: (cy: cytoscape.Core, res: any) => any;
    selectedLinks: any[];
    isSideBarOpen: boolean;
    DashboardComponent: any;
    loadAllForMap: (cy: cytoscape.Core, refresh: boolean) => void;
    eh: any;
  }) {
    //@ts-ignore
    cy.on('ehcomplete', (event, sourceNode, targetNode, addedEdge) => {
      // Only handle regular link creation, not unlinking or changing link source
      if (context.componentInstance.isUnlinking || context.componentInstance.isChangingLinkSource) {
        return;
      }

      this.cyService.linkingCompleteSubject.next(true);
      const currentState = context.mapStateService.getCurrentLocalState();
      const isDefaultState = !currentState?.id;
      if (context.hasMapPermission('edit')) {
        addedEdge.data('name', '');
        addedEdge.data('label', '');
        addedEdge.data('color', DefaultValues.link.color);
        addedEdge.data('weight', 1);
        addedEdge.data('type', 'BEZIER');
        addedEdge.data('textFontSize', DefaultValues.link.textFontSize);
        addedEdge.data('labelSlope', DefaultValues.link.labelSlope);
        addedEdge.data('shape', 'solid');
        addedEdge.data('linkLabelPosition', 0);
        addedEdge.data('linkLabelOffset', DefaultValues.link.linkLabelOffset);
        const from = { ...sourceNode.data() };
        const to = { ...targetNode.data() };
        delete from.collapsedChildren;
        delete to.collapsedChildren;

        this.nodeService.linkNodes(from, to)
          .pipe(
            delay(200)
          )
          .subscribe((res: any) => {

            // Unselect all elements first and clear selections
            cy.elements().unselect();
            sourceNode.unselect();
            targetNode.unselect();
            DashboardComponent.tappedNode = null;
            context.componentInstance.selectedNodes = [];

            const updateLinkOnCanvas = () => {
              // Apply model after link is created and displayed (if there's a pending model)
              if (this.pendingLinkModel && res.id) {
                const linkModel = this.pendingLinkModel;
                // Apply the model to the already created and displayed link
                this.modelService.apply(linkModel.id, {
                  elementIds: [res.id],
                  mapId: res.mapId || this.mapService.getCurrentSelectedMapFromStore()?.id,
                  inclusionOptions: linkModel.inclusionOptions
                }).subscribe({
                  next: (response) => {
                    cy.getElementById(res.id).remove();
                    if (response.success && response.updatedElements && response.updatedElements.length > 0) {
                      const modelLinkData = clearEdgeEditingData(response.updatedElements[0]);
                      const modelLinkType = modelLinkData.type;
                      delete modelLinkData.type;

                      const newLinkData = {
                        from: res.from,
                        to: res.to,
                        id: res.id,
                        ...modelLinkData,
                        type: modelLinkType
                      }

                      addedEdge.remove();

                      const controlOrBend = modelLinkType === 'STRAIGHT' ? 'bend' : modelLinkType === 'UNBUNDLED_BEZIER' ? 'control' : 'none';
                      const newLink = context.addCyLink(cy,newLinkData);
                      if (controlOrBend !== 'none') {
                        callAddAnchor(newLink, controlOrBend);
                      }
                      // MAP-1547: mark the model-created link as new (the regular,
                      // non-model branch already does this). Without it, naming the
                      // freshly created link is not folded into the creation entry,
                      // so undo takes two steps (name change, then creation) instead
                      // of one.
                      newLink.data('isNew', true);
                      context.selectedLinks = [];
                      setTimeout(() => {
                        cy.nodes().unselect();
                        sourceNode.unselect();
                        targetNode.unselect();

                        DashboardComponent.tappedNode = null;
                        context.componentInstance.selectedNodes = [];
                        newLink.select();
                        DashboardComponent.tappedLink = newLink.data();
                      }, 50);

                      // Push a single CreateLinkCommand capturing the fully
                      // model-populated link (style/properties included) so
                      // undo removes exactly what the model created in one
                      // step, instead of leaving the model's setup untracked
                      // as separate later entries (MAP-1547).
                      if (res.id && newLink) {
                        const linkSnapshot: GraphLinkSnapshot = {
                          id: res.id,
                          data: {
                            ...newLink.data(),
                            id: res.id,
                            source: res.from?.id || newLink.data('source'),
                            target: res.to?.id || newLink.data('target')
                          },
                          sourceId: res.from?.id || newLink.data('source'),
                          targetId: res.to?.id || newLink.data('target')
                        };
                        const createLinkCommand = new CreateLinkCommand(
                          this.cyService,
                          this.nodeService,
                          linkSnapshot
                        );
                        this.undoRedoManager.pushCommand(createLinkCommand);
                      }

                      return newLink;
                    }
                  },
                  error: (error: any) => {
                    console.error('Error applying link model:', error);
                  }
                });

                // Clear the pending model
                this.pendingLinkModel = null;
                this.sourceNodeForModel = null;
              } else {
                const linkEndpoint = context.linkEndpointSignal();
                if (linkEndpoint !== null) {
                  if (linkEndpoint.point === 'source') {
                    res.sourceEndpoint = linkEndpoint.direction;
                  } else {
                    res.targetEndpoint = linkEndpoint.direction;
                  }
                }
                const cleanedPayloadFromEdgeEditing = clearEdgeEditingData(res);
                cleanedPayloadFromEdgeEditing.type = 'BEZIER'; // Default to BEZIER on creation
                const link = context.addCyLink(cy, cleanedPayloadFromEdgeEditing);
                if (!link) return;
                addedEdge.remove();
                link.data('isNew', true);
                link.data('metadata', res.metadata || []);
                context.selectedLinks = [];
                // Ensure nodes stay unselected and only link is selected
                setTimeout(() => {
                  cy.nodes().unselect();
                  sourceNode.unselect();
                  targetNode.unselect();

                  DashboardComponent.tappedNode = null;
                  context.componentInstance.selectedNodes = [];
                  link.select();
                  DashboardComponent.tappedLink = link.data();

                  // Initialize anchor points AFTER selection is complete
                  if (link.data('type') !== 'BEZIER') {
                    //@ts-ignore
                    context.componentInstance.edgeEditingInstance.initAnchorPoints([link]);
                  }
                }, 50);
                return link;
              }
            };

            const currentState = this.mapStateService.getCurrentLocalState();
            const isDefaultState = !currentState?.id;
            if (!isDefaultState) {
              const state = {
                stateId: currentState?.id as string,
                link: {
                  id: res.id,
                  from: from.id,
                  to: to.id,
                  name: '',
                  label: '',
                  color: DefaultValues.link.color,
                  weight: 1,
                  type: 'BEZIER',
                  textFontSize: DefaultValues.link.textFontSize,
                  labelSlope: DefaultValues.link.labelSlope,
                  linkLabelOffset: DefaultValues.link.linkLabelOffset,
                  shape: 'solid'
                }
              };
              res.states = [state] as any;
              this.nodeService.updateLink(res).subscribe(() => {
                const link = updateLinkOnCanvas();
                // If link is null, it means model is being applied asynchronously
                if (link) {
                  link.data('isNotDefaultState', true);
                  link.data('states', [state]);
                  DashboardComponent.tappedLink = link.data();
                  DashboardComponent.tappedNode = null;
                }
              });

            } else {
              const link = updateLinkOnCanvas();
              setTimeout(() => {
                if (link) {
                  //@ts-ignore
                  DashboardComponent.tappedLink = link.data();
                  DashboardComponent.tappedNode = null;

                  // Create CreateLinkCommand for undo/redo
                  const linkSnapshot: GraphLinkSnapshot = {
                    id: res.id,
                    data: {
                      ...link.data(),
                      id: res.id,
                      source: res.from?.id || link.data('source'),
                      target: res.to?.id || link.data('target')
                    },
                    sourceId: res.from?.id || link.data('source'),
                    targetId: res.to?.id || link.data('target')
                  };
                  const createLinkCommand = new CreateLinkCommand(
                    this.cyService,
                    this.nodeService,
                    linkSnapshot
                  );
                  this.undoRedoManager.pushCommand(createLinkCommand);
                }
                if (res) {
                  const freshLink =  cy.getElementById(res.id);
                  const pointData = getPointDataFromAnchorsArray(freshLink, context.componentInstance.edgeEditingInstance);

                  if (pointData) {
                    freshLink.data(pointData?.dataKey, pointData?.pointData);
                  }
                  context.componentInstance.updateLink(freshLink.data(), true).subscribe();
                }
              }, 300);

            }
            DashboardComponent.tappedNode = null;
            context.componentInstance.isSideBarOpen = true;
            DashboardComponent.multiSelectedElements = [];
          });
      }
    });
    //@ts-ignore
    cy.on('ehcancel', (event, source, canceledTarget) => {
      context.componentInstance.isUnlinking = false;
      context.componentInstance.isChangingLinkSource = false;
      // No need to reload all data on link cancellation - just reset flags
      // context.loadAllForMap(cy, false);
    });

    //@ts-ignore
    cy.on('ehstart', (event) => {
      //@ts-ignore
      context.eh.update(context.cyService.getMousePosition());
    });
  }

  private explainLink(data: GraphNodeLink) {
    if (data.aiGeneratedDescription) {
      this.ttsService.speakAIDescription(data.aiGeneratedDescription).then();
    } else {
      if(!data) return;
      const currentMap = this.mapService.getCurrentSelectedMapFromStore();

      this.openaiService.describeLink(data.id as string, currentMap.id)
        .subscribe({
          next: (job) => {
            let attempts = 0;
            const maxAttempts = 30; // Maximum 60 seconds of polling (30 * 2 seconds)

            const interval = setInterval(() => {
              attempts++;

              // Safety check: stop polling after max attempts
              if (attempts >= maxAttempts) {
                console.warn(`Job ${job.id} timed out after ${maxAttempts * 2} seconds`);
                this.notificationService.error(this.translate.instant('DASHBOARD_MSG.AI_DESCRIPTION.TIMEOUT'), {
                  title: this.translate.instant('DASHBOARD_MSG.AI_DESCRIPTION.FAILED_TITLE'),
                  duration: 0 // Persistent until dismissed
                });
                clearInterval(interval);
                return;
              }

              this.jobService.getJob(job.id).subscribe((jobData) => {
                if (jobData.status === 'COMPLETED') {
                  const description = jobData.result?.rawData;
                  if (description.type === 'text') {
                    const value = JSON.parse(description.text.value);
                    this.ttsService.speakAIDescription(value?.description).then();
                  }
                  clearInterval(interval);
                } else if (jobData.status === 'FAILED') {
                  console.warn(jobData.error);
                  this.notificationService.error(jobData.error || this.translate.instant('DASHBOARD_MSG.AI_DESCRIPTION.GENERATION_FAILED'), {
                    title: this.translate.instant('DASHBOARD_MSG.AI_DESCRIPTION.FAILED_TITLE'),
                    duration: 0 // Persistent until dismissed
                  });
                  clearInterval(interval);
                }
                // If status is PENDING, it will continue until maxAttempts
              });
            }, 2000);
          },
          error: (error) => {
            const errorMessage = error.error?.message || error.message || this.translate.instant('DASHBOARD_MSG.AI_DESCRIPTION.GENERIC_ERROR');
            this.notificationService.error(errorMessage, {
              title: 'AI Description Failed',
              duration: 0 // Persistent until dismissed
            });
          }
        });
    }
  }

  private explainNode(data: GraphNode) {
    if (data.aiGeneratedDescription) {
      this.ttsService.speakAIDescription(data.aiGeneratedDescription).then();
    } else {
      if(!data) return;
      const currentMap = this.mapService.getCurrentSelectedMapFromStore();

      this.openaiService.describeNode(data.id as string, currentMap.id)
        .subscribe({
          next: (job) => {
            let attempts = 0;
            const maxAttempts = 30; // Maximum 60 seconds of polling (30 * 2 seconds)

            const interval = setInterval(() => {
              attempts++;

              // Safety check: stop polling after max attempts
              if (attempts >= maxAttempts) {
                console.warn(`Job ${job.id} timed out after ${maxAttempts * 2} seconds`);
                this.notificationService.error(this.translate.instant('DASHBOARD_MSG.AI_DESCRIPTION.TIMEOUT'), {
                  title: this.translate.instant('DASHBOARD_MSG.AI_DESCRIPTION.FAILED_TITLE'),
                  duration: 0 // Persistent until dismissed
                });
                clearInterval(interval);
                return;
              }

              this.jobService.getJob(job.id).subscribe((jobData) => {
                if (jobData.status === 'COMPLETED') {
                  const description = jobData.result?.rawData;
                  if (description.type === 'text') {
                    const value = JSON.parse(description.text.value);
                    this.ttsService.speakAIDescription(value?.description).then();
                  }
                  clearInterval(interval);
                } else if (jobData.status === 'FAILED') {
                  console.warn(jobData.error);
                  this.notificationService.error(jobData.error || this.translate.instant('DASHBOARD_MSG.AI_DESCRIPTION.GENERATION_FAILED'), {
                    title: this.translate.instant('DASHBOARD_MSG.AI_DESCRIPTION.FAILED_TITLE'),
                    duration: 0 // Persistent until dismissed
                  });
                  clearInterval(interval);
                }
                // If status is PENDING, it will continue until maxAttempts
              });
            }, 2000);
          },
          error: (error) => {
            const errorMessage = error.error?.message || error.message || this.translate.instant('DASHBOARD_MSG.AI_DESCRIPTION.GENERIC_ERROR');
            this.notificationService.error(errorMessage, {
              title: 'AI Description Failed',
              duration: 0 // Persistent until dismissed
            });
          }
        });
    }
  }

  /**
   * Update undo/redo menu items without rebuilding the entire context menu.
   * Uses the context menu instance API for enable/disable and DOM manipulation for text updates.
   */
  private updateUndoRedoMenuItems(contextMenuInstance: any): void {
    if (!contextMenuInstance) return;

    try {
      const canUndo = this.undoRedoManager.canUndo();
      const canRedo = this.undoRedoManager.canRedo();

      // Update all undo items (canvas, node, edge)
      const undoIds = ['undo', 'undoNode', 'undoEdge'];
      for (const id of undoIds) {
        if (canUndo) {
          contextMenuInstance.enableMenuItem(id);
        } else {
          contextMenuInstance.disableMenuItem(id);
        }
      }

      // Update all redo items (canvas, node, edge)
      const redoIds = ['redo', 'redoNode', 'redoEdge'];
      for (const id of redoIds) {
        if (canRedo) {
          contextMenuInstance.enableMenuItem(id);
        } else {
          contextMenuInstance.disableMenuItem(id);
        }
      }

      // Update content text via DOM manipulation
      // The menu items have their id directly set (e.g., 'undo', 'redo')
      // Structure is: <button id="undo"><img ... />Undo text</button>
      const undoText = canUndo ? this.translate.instant('DASHBOARD_MSG.MENU.UNDO_LABELED', { description: this.undoRedoManager.undoDescription() }) : this.translate.instant('TOOLBAR.UNDO');
      const redoText = canRedo ? this.translate.instant('DASHBOARD_MSG.MENU.REDO_LABELED', { description: this.undoRedoManager.redoDescription() }) : this.translate.instant('TOOLBAR.REDO');

      // Find all undo/redo button elements and update text content
      for (const id of undoIds) {
        const button = document.getElementById(id);
        if (button) {
          const img = button.querySelector('img');
          button.innerHTML = '';
          if (img) button.appendChild(img);
          button.append(undoText);
        }
      }
      for (const id of redoIds) {
        const button = document.getElementById(id);
        if (button) {
          const img = button.querySelector('img');
          button.innerHTML = '';
          if (img) button.appendChild(img);
          button.append(redoText);
        }
      }
    } catch (error) {
      // Silently ignore errors - menu may not be rendered yet
    }
  }
}
